import React, { useState } from 'react';
import { useDashboard } from '../../../hooks/useDashboard';
import { useAreas } from '../../../hooks/useAreas';
import { useTareas } from '../../../hooks/useTareas';
import { useRegistrosLimpieza } from '../../../hooks/useRegistrosLimpieza';
import { useRutasByRangoFechas } from '../../../hooks/useRegistrosLimpieza';
import DateRangeFilter, { DateFilterOptions } from '../../../components/DateRangeFilter';
import { getEffectiveDateRange, filterRegistrosByDateRange, getDateRangeLabel } from '../../../utils/dateFilterUtils';
import { 
  DashboardReportGenerator, 
  TendenciasReportGenerator, 
  AdvancedReportGenerator,
  RutasReportGenerator
} from '../components';

const ReportesPage: React.FC = () => {
  const { metrics, isLoading: isLoadingMetrics, error: errorMetrics } = useDashboard();
  const { areas, isLoading: areasLoading, error: areasError } = useAreas();
  const { tareas, isLoading: tareasLoading, error: tareasError } = useTareas();
  const { registrosLimpieza, isLoading: registrosLoading, error: registrosError } = useRegistrosLimpieza();
  
  const [dateFilter, setDateFilter] = useState<DateFilterOptions>({
    mode: 'preset',
    preset: 'hoy'
  });
  
  // Obtener rutas y usuarios disponibles basado en el nuevo filtro
  const dateRange = React.useMemo(() => getEffectiveDateRange(dateFilter), [dateFilter]);
  
  const { data: rutasResponse, isLoading: rutasLoading } = useRutasByRangoFechas(
    dateRange.startDate, 
    dateRange.endDate
  );
  const rutas = React.useMemo(() => rutasResponse?.data || [], [rutasResponse?.data]);
  
  // Obtener usuarios únicos de las rutas
  const usuariosDisponibles = React.useMemo(() => {
    const usuariosMap = new Map<number, { id: number; nombre: string }>();
    rutas.forEach(ruta => {
      if (!usuariosMap.has(ruta.usuarioId)) {
        usuariosMap.set(ruta.usuarioId, {
          id: ruta.usuarioId,
          nombre: ruta.usuarioNombre
        });
      }
    });
    return Array.from(usuariosMap.values());
  }, [rutas]);

  // Cálculo de datos para reportes con filtros aplicados
  const chartData = React.useMemo(() => {
    // Aplicar filtros de fecha usando el nuevo sistema
    const registrosFiltrados = filterRegistrosByDateRange(registrosLimpieza, dateRange);
    
    // Usar la misma lógica limpia que el Dashboard
    const startLocal = new Date(dateRange.startDate + 'T00:00:00');
    const endLocal   = dateFilter.mode === 'date' || (dateFilter.mode === 'preset' && dateFilter.preset === 'hoy')
      ? new Date(dateRange.startDate + 'T00:00:00')
      : new Date(dateRange.endDate + 'T00:00:00');

    const esUnDia = dateFilter.mode === 'date' || (dateFilter.mode === 'preset' && dateFilter.preset === 'hoy');
    const rangoDias = esUnDia ? 1 : Math.ceil((endLocal.getTime() - startLocal.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    const fechas: string[] = Array.from({ length: rangoDias }, (_, i) => {
      const f = new Date(startLocal);
      f.setDate(f.getDate() + i);
      const year = f.getFullYear();
      const month = String(f.getMonth() + 1).padStart(2, '0');
      const day = String(f.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    });

    const registrosPorDia = fechas.map(fecha => {
      return registrosFiltrados.filter(r => {
        try {
          // Convertir la fecha del registro a formato local YYYY-MM-DD
          const fechaRegistro = new Date(r.fechaHoraInicio);
          const year = fechaRegistro.getFullYear();
          const month = String(fechaRegistro.getMonth() + 1).padStart(2, '0');
          const day = String(fechaRegistro.getDate()).padStart(2, '0');
          const fechaRegistroFormateada = `${year}-${month}-${day}`;
          
          return fechaRegistroFormateada === fecha;
        } catch {
          return false;
        }
      }).length;
    });

    return {
      labels: fechas.map(ymd => {
        const [, m, d] = ymd.split('-');
        return `${d}/${m}`;
      }),
      datasets: [{
        label: `Registros de Limpieza (${getDateRangeLabel(dateFilter)})`,
        data: registrosPorDia,
        backgroundColor: ['rgba(59, 130, 246, 0.2)'],
        borderColor: ['rgba(59, 130, 246, 1)'],
        borderWidth: 2
      }]
    };
  }, [registrosLimpieza, dateFilter, dateRange]);

  // Agrupar registros por obra con filtros aplicados
  const registrosPorObra = React.useMemo(() => {
    // Aplicar filtros de fecha usando el nuevo sistema
    const registrosFiltrados = filterRegistrosByDateRange(registrosLimpieza, dateRange);

    const grupos = registrosFiltrados.reduce((acc, registro) => {
      const obra = 'Obra Principal';
      if (!acc[obra]) {
        acc[obra] = [];
      }
      acc[obra].push(registro);
      return acc;
    }, {} as Record<string, typeof registrosLimpieza>);

    return Object.entries(grupos).map(([obra, registros]) => ({
      obra,
      registros: registros.slice(0, 5),
      total: registros.length,
      completados: registros.filter(r => r.fechaHoraFin).length,
      pendientes: registros.filter(r => !r.fechaHoraFin).length
    }));
  }, [registrosLimpieza, dateRange]);

  // Calcular métricas filtradas localmente
  const metricsFiltered = React.useMemo(() => {
    // Aplicar filtros de fecha usando el nuevo sistema
    const registrosFiltrados = filterRegistrosByDateRange(registrosLimpieza, dateRange);
    


    // Calcular métricas basadas en los registros filtrados
    const tareasActivas = tareas.filter(t => t.activo);
    
    // Calcular registros del período seleccionado (con filtros aplicados)
    const registrosHoy = registrosFiltrados.length;

    const totalRegistros = registrosFiltrados.length;
    const registrosCompletados = registrosFiltrados.filter(r => r.fechaHoraFin).length;
    const porcentajeCompletados = totalRegistros > 0 ? Math.round((registrosCompletados / totalRegistros) * 100) : 0;

    return {
      totalAreas: areas.length,
      tareasActivas: tareasActivas.length,
      totalTareas: tareas.length,
      registrosHoy: registrosHoy,
      porcentajeCompletados,
      areasPendientes: areas.filter(a => a.estado === 'Pendiente').length,
      areasCompletadas: areas.filter(a => a.estado === 'Completada').length,
      areasPorEstado: metrics?.areasPorEstado || [],
      registrosPorDia: [],
      registrosSinFinalizar: registrosFiltrados.filter(r => !r.fechaHoraFin).length,
      areasUrgentes: 0, // Se eliminó de los reportes
      areasUrgentesDetalle: [] // Se eliminó de los reportes
    };
  }, [registrosLimpieza, areas, tareas, dateRange, metrics?.areasPorEstado]);

  const fechaGeneracion = new Date().toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  if (areasLoading || tareasLoading || registrosLoading || isLoadingMetrics || rutasLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando reportes...</div>
      </div>
    );
  }

  if (areasError || tareasError || registrosError || errorMetrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          Error al cargar los datos. Intenta recargar la página.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-2 sm:px-6 lg:px-8">
      <div className="py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Módulo de Reportes</h1>
          <p className="mt-2 text-gray-600">
            Genera reportes profesionales en PDF con análisis detallado de datos
          </p>
        </div>

        {/* Información del módulo */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-3">
            Acerca del Módulo de Reportes
          </h3>
          <div className="text-blue-800 text-sm space-y-2">
            <p>
              Este módulo te permite generar reportes profesionales en PDF con diferentes niveles de detalle:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li><strong>Reporte del Dashboard:</strong> Vista general con métricas principales</li>
              <li><strong>Reporte de Tendencias:</strong> Análisis de patrones y variabilidad</li>
              <li><strong>Reporte Avanzado:</strong> Estadísticas detalladas con gráficos</li>
              <li><strong>Reporte de Rutas:</strong> Análisis detallado de rutas por usuario</li>
            </ul>
            <p className="mt-3 text-blue-700">
              Los reportes se generan automáticamente y se descargan en formato PDF para su uso posterior.
            </p>
          </div>
        </div>

        {/* Filtros */}
        <DateRangeFilter
          value={dateFilter}
          onChange={setDateFilter}
          className="mb-6"
        />

        {/* Generadores de Reportes */}
        <DashboardReportGenerator 
          metrics={metricsFiltered}
          registrosPorObra={registrosPorObra}
          chartData={chartData}
          fechaGeneracion={fechaGeneracion}
          filtroFecha={getDateRangeLabel(dateFilter)}
        />

        <TendenciasReportGenerator 
          chartData={chartData}
          registrosPorObra={registrosPorObra}
          fechaGeneracion={fechaGeneracion}
          filtroFecha={getDateRangeLabel(dateFilter)}
        />

        <AdvancedReportGenerator 
          chartData={chartData}
          registrosPorObra={registrosPorObra}
          fechaGeneracion={fechaGeneracion}
        />

        <RutasReportGenerator 
          rutas={rutas}
          fechaGeneracion={fechaGeneracion}
          filtroFecha={getDateRangeLabel(dateFilter)}
          usuariosDisponibles={usuariosDisponibles}
        />

      </div>
    </div>
  );
};

export default ReportesPage;